import { ReactNode } from "react";
import { formatDistanceToNow } from "date-fns";
import { Offer } from "./model";
import { ColumnDef } from "@tanstack/react-table";
import { EllipsisVerticalIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Link, NavLink } from "react-router-dom";
import { Dialog, DialogTrigger } from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import { extractFromMultiName, extractFromMultiNameList, getEarliestDate, getLatestDate } from "../Locations/utils";

export const offersColumns: ColumnDef<Offer>[] = [
  {
    header: "Client",
    cell: ({ row }) => {
      const c: any = row.original.customer;
      const name = c?.fullName || [c?.firstName, c?.lastName].filter(Boolean).join(" ") || "—";
      return <span className="font-semibold whitespace-nowrap">{name}</span>;
    },
  },
  {
    header: "Contents",
    cell: ({ row }) => {
      const yachts: any[] = row.original.yachts ?? [];
      const region =
        (row.original as any).startLocation?.names?.split?.(",")?.[0] ??
        yachts[0]?.yacht?.location?.town?.country?.names?.split?.(",")?.[0] ??
        null;
      return (
        <span className="text-slate-600 whitespace-nowrap">
          {yachts.length} yacht{yachts.length === 1 ? "" : "s"}
          {region ? ` · ${region}` : ""}
        </span>
      );
    },
  },
  {
    header: "Status",
    cell: ({ row }) => {
      const o: any = row.original;
      const yachts: any[] = o.yachts ?? [];
      const loves = yachts.filter((y) => y?.reaction === "love").length;
      const passes = yachts.filter((y) => y?.reaction === "pass").length;
      const maybes = yachts.filter((y) => y?.reaction === "maybe").length;
      const held = yachts.filter((y) => y?.yachtOnHold || (y?.holdExpiresAt && new Date(y.holdExpiresAt) > new Date()));
      const expiries = held
        .map((y) => (y?.holdExpiresAt ? new Date(y.holdExpiresAt).getTime() : null))
        .filter((t): t is number => t !== null);
      const soonest = expiries.length ? new Date(Math.min(...expiries)) : null;
      const chip = (cls: string, label: ReactNode) => (
        <span className={`inline-flex items-center gap-1 py-1 px-2.5 rounded-full text-sm font-medium whitespace-nowrap ${cls}`}>
          {label}
        </span>
      );
      if (o.status === "new_booking") return chip("bg-green-100 text-green-700", "Accepted");
      if (held.length)
        return (
          <span className="flex flex-col gap-0.5 items-start">
            {chip("bg-teal-100 text-teal-700", "On option")}
            {soonest && (
              <span className="text-xs text-teal-600 whitespace-nowrap">
                Option expires {formatDistanceToNow(soonest, { addSuffix: true })}
              </span>
            )}
          </span>
        );
      if (loves + passes + maybes > 0)
        return chip(
          "bg-blue-100 text-blue-700",
          <>Reacted{loves ? ` · ${loves}❤` : ""}{maybes ? ` · ${maybes}◐` : ""}{passes ? ` · ${passes}✗` : ""}</>,
        );
      if ((o.views ?? 0) > 0) return chip("bg-blue-50 text-blue-600", `Viewed ${o.views}×`);
      if (o.status === "broker_offer_sent" || o.status === "automatic_offer_sent")
        return chip("bg-slate-100 text-slate-600", "Sent");
      return chip("bg-amber-100 text-amber-700", "Draft");
    },
  },
  {
    header: "Chosen / option",
    cell: ({ row }) => {
      const yachts: any[] = row.original.yachts ?? [];
      const chosen =
        yachts.find((y) => y?.reaction === "love") ??
        yachts.find((y) => y?.yachtOnHold || (y?.holdExpiresAt && new Date(y.holdExpiresAt) > new Date()));
      if (!chosen?.yacht?.name) return <span className="text-slate-300">—</span>;
      return <span className="font-medium whitespace-nowrap uppercase">{chosen.yacht.name}</span>;
    },
  },
  {
    header: "Value",
    cell: ({ row }) => {
      const o: any = row.original;
      // An offer is a set of alternatives, not a basket — the client picks ONE. Summing
      // them produced a meaningless number (3 yachts read as €775k), so show the average
      // option value, which is what the offer is actually worth.
      const priced = (o.yachts ?? [])
        .map((y: any) => y?.discountCharterPrice ?? y?.pricing?.totalPrice ?? 0)
        .filter((v: number) => v > 0);
      if (!priced.length) return <span className="text-slate-300">—</span>;
      const avg = priced.reduce((s: number, v: number) => s + v, 0) / priced.length;
      const code = o.currency?.code ?? "EUR";
      return (
        <span
          className="font-medium whitespace-nowrap"
          title={priced.length > 1 ? `Average of ${priced.length} options` : undefined}
        >
          {new Intl.NumberFormat("en-EN", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(avg)}
        </span>
      );
    },
  },
  {
    header: "Updated",
    cell: ({ row }) => {
      const d = (row.original as any).dateUpdated ?? row.original.dateCreated;
      if (!d) return <span className="text-slate-300">—</span>;
      return <span className="text-slate-500 whitespace-nowrap">{formatDistanceToNow(new Date(d), { addSuffix: true })}</span>;
    },
  },
  {
    id: "actions",
    size: 32,
    cell({ row }) {
      return (
        // <Popover>
        //   <PopoverTrigger
        //     onClick={(e) => {
        //       e.stopPropagation();
        //     }}
        //   >
        //     <EllipsisVerticalIcon />
        //   </PopoverTrigger>
        //   <PopoverContent align="end" className="w-[172px] flex flex-col border border-color-slate-200 rounded-md p-0">

        //   </PopoverContent>
        // </Popover>
        <Dialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="p-1 h-fit">
                <EllipsisVerticalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DialogTrigger asChild>
                <DropdownMenuItem className="p-0">
                  <NavLink
                    className="py-1.5 px-2"
                    onClick={(e) => e.stopPropagation()}
                    to={`/offers/${row.original.id}`}
                  >
                    See details
                  </NavLink>
                </DropdownMenuItem>
              </DialogTrigger>
              <DropdownMenuItem
                className="py-1.5 px-2 cursor-pointer"
                onClick={async (e) => {
                  e.stopPropagation();
                  // Duplicate-to-reuse: clone into a fresh draft and jump into it.
                  const { fetch } = await import("@/src/lib/fetch");
                  const r = await fetch<{ data: { id: string } }>(
                    `/api/offers/${row.original.id}/duplicate`,
                    "POST",
                  );
                  if (r?.data?.id) window.location.assign(`/offers/edit/${r.data.id}`);
                }}
              >
                Duplicate
              </DropdownMenuItem>
              <DialogTrigger asChild>
                <DropdownMenuItem className="p-0">
                  {row.original.status !== "broker_offer_sent" && (
                    <NavLink
                      className="py-1.5 px-2"
                      onClick={(e) => e.stopPropagation()}
                      to={`/offers/edit/${row.original.id}?step=Customer`}
                    >
                      Edit
                    </NavLink>
                  )}
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogTrigger asChild>
                <DropdownMenuItem className="p-0">
                  <NavLink
                    className="py-1.5 px-2"
                    onClick={(e) => e.stopPropagation()}
                    to={`/offers/${row.original.id}?modal=versions`}
                  >
                    See versions
                  </NavLink>
                </DropdownMenuItem>
              </DialogTrigger>
            </DropdownMenuContent>
          </DropdownMenu>
        </Dialog>
      );
    },
  },
];
